import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { sb } from '../supabase';

// The notification bell: the last ten notifications, live (realtime + a 30 s
// poll), marked read on open, each one routed to where it belongs. Moved out
// of Navbar.jsx unchanged so the site navbar and the marketplace header (one
// header on market pages) show the SAME bell.
export default function NotificationBell({ user, lang }) {
  const nav = useNavigate();
  const isAr = lang === 'ar';
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs]       = useState([]);
  const [unread, setUnread]       = useState(0);
  const notifRef = useRef(null);

  /* ─── Close notifs on outside click ─────── */
  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target))
        setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ─── Notifications ─────────────────────── */
  useEffect(() => {
    if (!user) return;
    loadNotifs();
    const interval = setInterval(loadNotifs, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const loadNotifs = async () => {
    const { data } = await sb
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      // Bookkeeping markers App.js writes (already read) to remember an email
      // was sent — not messages for a person. The push trigger skips them too.
      .not('type', 'in', '(trader_welcome_sent,supplier_admin_notified)')
      .order('created_at', { ascending: false })
      .limit(10);
    if (data) {
      setNotifs(data);
      setUnread(data.filter(n => !n.is_read).length);
    }
  };

  /* ─── Realtime: new notification ─────── */
  useEffect(() => {
    if (!user) return;
    const channel = sb
      .channel(`navbar-notifs-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, () => { loadNotifs(); })
      .subscribe();
    return () => sb.removeChannel(channel);
  }, [user]);

  /* ─── Sync counter when chat is opened ─ */
  useEffect(() => {
    const handler = () => { if (user) loadNotifs(); };
    window.addEventListener('maabar:messages-read', handler);
    return () => window.removeEventListener('maabar:messages-read', handler);
  }, [user]);

  const openNotifs = async () => {
    setNotifOpen(!notifOpen);
    if (!notifOpen && unread > 0) {
      await sb.from('notifications').update({ is_read: true })
        .eq('user_id', user.id).eq('is_read', false);
      await loadNotifs();
    }
  };

  const handleNotifClick = async (n) => {
    setNotifOpen(false);
    if (n.type === 'new_message') nav(`/chat/${n.ref_id}`);
    else if (['account_approved', 'account_rejected'].includes(n.type)) nav('/dashboard');
    else if (['account_more_info_required', 'verification_submitted'].includes(n.type)) nav('/dashboard?tab=verification');
    else if (n.type === 'direct_order_pending') nav('/dashboard?tab=direct-orders');
    else if (['payment_received', 'shipped', 'arrived', 'delivery_confirmed', 'supplier_confirmed'].includes(n.type)) {
      // Shared between RFQ and Direct Purchase — disambiguate via requests.product_ref.
      if (!n.ref_id) { nav('/dashboard?tab=requests'); return; }
      const lookupRes = await sb
        .from('requests')
        .select('product_ref')
        .eq('id', n.ref_id)
        .single();
      console.log('[handleNotifClick] requests lookup for shared type:', { type: n.type, ref_id: n.ref_id, response: lookupRes });
      if (lookupRes.data?.product_ref) nav('/dashboard?tab=direct-orders');
      else nav('/dashboard?tab=requests');
    }
    // A request routed to a supplier, or a new one in their category (migration 20261014164759).
    else if (['request_routed', 'request_available'].includes(n.type)) nav('/dashboard?tab=requests');
    else if (['new_offer', 'offer_accepted', 'offer_rejected', 'offer_cancelled', 'ready_to_ship', 'request_deleted', 'supplier_rejected'].includes(n.type)) nav('/dashboard?tab=requests');
    else if (['new_sample', 'sample_approved', 'sample_rejected'].includes(n.type)) nav('/dashboard?tab=samples');
    else if (['product_inquiry', 'product_inquiry_reply'].includes(n.type)) nav('/dashboard?tab=product-inquiries');
    // HioBuy marketplace order updates (migration market_orders, phase 3).
    else if (n.type === 'market_order_update' && n.ref_id) nav(`/market/orders/${n.ref_id}`);
    else if (n.type === 'market_inquiry_reply' && n.ref_id) nav(`/market/inquiries/${n.ref_id}`);
    else nav('/dashboard');
  };

  const fmtTime = (d) => {
    const diff = Math.floor((Date.now() - new Date(d)) / 1000);
    if (diff < 3600)  return isAr ? Math.floor(diff / 60) + ' د'   : Math.floor(diff / 60) + 'm';
    if (diff < 86400) return isAr ? Math.floor(diff / 3600) + ' س' : Math.floor(diff / 3600) + 'h';
    return isAr ? Math.floor(diff / 86400) + ' ي' : Math.floor(diff / 86400) + 'd';
  };

  /* ─── Bell SVG ────────────────────────────── */
  const BellIcon = () => (
    <svg viewBox="0 0 24 24">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );


  return (
              <div ref={notifRef} style={{ position: 'relative' }}>
                <button className="icon-btn" onClick={openNotifs}
                  title={isAr ? 'الإشعارات' : 'Notifications'}>
                  <BellIcon />
                  <span className={`icon-badge${unread > 0 ? ' show' : ''}`}>
                    {unread > 9 ? '9+' : unread}
                  </span>
                </button>

                {/* Notifications dropdown */}
                {notifOpen && (
                  <div className="notif-panel open" style={{
                    [isAr ? 'left' : 'right']: 0,
                  }}>
                    <div className="notif-header" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      ...( isAr ? { fontFamily: 'var(--font-ar)' } : {} ),
                    }}>
                      {isAr ? 'الإشعارات' : lang === 'zh' ? '通知' : 'Notifications'}
                      {unread > 0 && (
                        <span style={{
                          background: 'var(--bg-raised)',
                          border: '1px solid var(--border-muted)',
                          color: 'var(--text-secondary)',
                          fontSize: 8,
                          fontWeight: 600,
                          borderRadius: '50%',
                          width: 14, height: 14,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          {unread}
                        </span>
                      )}
                    </div>

                    {notifs.length === 0 ? (
                      <div className="notif-empty">
                        {isAr ? 'لا توجد إشعارات' : lang === 'zh' ? '暂无通知' : 'No notifications'}
                      </div>
                    ) : notifs.map((n, i) => (
                      <div key={i} className="notif-item"
                        onClick={() => handleNotifClick(n)}
                        style={{
                          background: n.is_read ? 'transparent' : 'var(--bg-hover)',
                        }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <p style={{
                            fontSize: 12,
                            color: 'var(--text-secondary)',
                            lineHeight: 1.6,
                            fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                            flex: 1,
                          }}>
                            {isAr ? n.title_ar : lang === 'zh' ? n.title_zh : n.title_en}
                          </p>
                          <span style={{ fontSize: 10, color: 'var(--text-disabled)', flexShrink: 0, marginTop: 2 }}>
                            {fmtTime(n.created_at)}
                          </span>
                        </div>
                        {!n.is_read && (
                          <div style={{
                            width: 4, height: 4,
                            borderRadius: '50%',
                            background: 'var(--accent)',
                            marginTop: 6,
                            opacity: 0.7,
                          }} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
  );
}
